import { memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import { ImageRequestDTO } from "@zenith/services/user";

// Same SmileID web component already used (and proven working) by
// profile-photo-verification-component.tsx for profile photo verification.
// The facial step-up flows (BOAS-8200/8203/7938/8197/8144) were built
// against a plain react-webcam capture (CameraComponent) instead — it
// produces the right *shape* (8 images, SELFIE_BASE64 + 7×LIVENESS_BASE64)
// but not a real liveness sequence, since it's just 8 frames grabbed on a
// fixed timer with no prompted movement. SmileID's backend rejects that
// with a VERIFICATION_ERROR. This component wraps the same SmileID capture
// widget so the facial step-up flows produce images SmileID actually
// accepts, without changing anything downstream — same
// {open, setOpen, getPhotos} interface as CameraComponent.
const useSmileIdWebComponent = () => {
  const [isComponentLoaded, setIsComponentLoaded] = useState(false);

  useEffect(() => {
    // v11 loads as an ES module, so the custom element may not be defined
    // synchronously by the time the script's onload fires - matches
    // profile-photo-verification-component.tsx / sign-up-photo-verification-page.tsx
    // (PR 8171) exactly, including the script id, so all three share one
    // cached <script> tag rather than loading the SDK three times.
    const markReady = () => {
      if (
        customElements.get("selfie-capture-screens") ||
        customElements.get("smart-camera-web")
      ) {
        setIsComponentLoaded(true);
        return;
      }
      Promise.race([
        customElements.whenDefined("smart-camera-web"),
        customElements.whenDefined("selfie-capture-screens"),
      ]).then(() => setIsComponentLoaded(true));
    };

    const existingScript = document.getElementById("smile-id-script-v11");
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = "smile-id-script-v11";
      script.type = "module";
      script.src = "https://cdn.smileidentity.com/js/v11/smart-camera-web.js";
      script.onload = markReady;
      script.onerror = () => console.error("Failed to load Smile ID script");
      document.head.appendChild(script);
    } else {
      markReady();
    }
  }, []);

  return { isComponentLoaded };
};

const cleanBase64 = (base64String: string): string => {
  if (base64String.startsWith("data:image/")) {
    return base64String.split(",")[1];
  }
  return base64String;
};

const convertSmileIdToApiFormat = (
  smileIdImages: { image: string }[]
): ImageRequestDTO[] =>
  smileIdImages.map((img, index) => ({
    imageContent: cleanBase64(img.image),
    imageType:
      index === smileIdImages.length - 1
        ? ImageRequestDTO.imageType.SELFIE_BASE64
        : ImageRequestDTO.imageType.LIVENESS_BASE64,
    mimeExt: "jpeg",
  }));

export interface SmileIdCameraComponentProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getPhotos?: (photos: ImageRequestDTO[]) => void;
}

const SmileIdCameraComponent = memo(function SmileIdCameraComponent(
  props: SmileIdCameraComponentProps
) {
  const { open, setOpen, getPhotos } = props;
  const smileIdRef = useRef<HTMLElement | null>(null);
  const { isComponentLoaded } = useSmileIdWebComponent();

  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  // Inject CSS into SmileID's nested shadow DOM (same styling patch as
  // profile-photo-verification-component.tsx).
  useEffect(() => {
    if (!open) return;

    const el = smileIdRef.current;
    if (!el) return;

    const outerShadow = (el as HTMLElement & { shadowRoot: ShadowRoot | null })
      .shadowRoot;

    const STYLE_CONTENT = `
      #selfie-capture-instruction-screen { padding-block: 0.75rem !important; }
      header svg { height: 40px !important; width: auto !important; }
      header { padding-block: 4px !important; }
      .instructions-wrapper { gap: 0.625rem !important; margin-block-start: 0.5rem !important; margin-block-end: 0.5rem !important; }
      .instructions svg { margin-inline-end: 0.75rem !important; }
      .flow > * + * { margin-top: 0.5rem !important; }
    `;

    let injectedSheet: CSSStyleSheet | null = null;
    let lastInnerShadow: ShadowRoot | null = null;

    const getInnerShadow = (): ShadowRoot | null => {
      const instructionEl =
        (el as HTMLElement).querySelector("selfie-capture-instructions") ??
        outerShadow?.querySelector("selfie-capture-instructions");
      if (!instructionEl) return null;
      return (
        (instructionEl as HTMLElement & { shadowRoot: ShadowRoot | null })
          .shadowRoot ?? null
      );
    };

    const overrideInlineFlowSpace = (shadow: ShadowRoot) => {
      shadow.querySelectorAll<HTMLElement>("section.flow").forEach((flowEl) => {
        if (flowEl.style.getPropertyValue("--flow-space") !== "0.75rem") {
          flowEl.style.setProperty("--flow-space", "0.75rem");
        }
      });
    };

    const injectIntoInnerShadow = () => {
      const innerShadow = getInnerShadow();
      if (!innerShadow) return;

      if (innerShadow !== lastInnerShadow) {
        lastInnerShadow = innerShadow;
        try {
          injectedSheet = new CSSStyleSheet();
          injectedSheet.replaceSync(STYLE_CONTENT);
          innerShadow.adoptedStyleSheets = [
            ...innerShadow.adoptedStyleSheets,
            injectedSheet,
          ];
        } catch {
          const STYLE_ID = "zenz-smileid-step-up-styles";
          if (!innerShadow.getElementById(STYLE_ID)) {
            const style = document.createElement("style");
            style.id = STYLE_ID;
            style.textContent = STYLE_CONTENT;
            innerShadow.appendChild(style);
          }
        }
      }

      overrideInlineFlowSpace(innerShadow);
    };

    injectIntoInnerShadow();

    const observer = new MutationObserver(injectIntoInnerShadow);
    observer.observe(el as HTMLElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      observer.disconnect();
      if (injectedSheet && lastInnerShadow) {
        lastInnerShadow.adoptedStyleSheets =
          lastInnerShadow.adoptedStyleSheets.filter((s) => s !== injectedSheet);
      }
    };
  }, [open]);

  useEffect(() => {
    if (!isComponentLoaded || !open) return;

    const smileIdElement = smileIdRef.current;
    if (!smileIdElement) return;

    const handleSelfieCapture = (event: CustomEvent) => {
      if (event.detail && event.detail.images) {
        const converted = convertSmileIdToApiFormat(event.detail.images);
        setOpen(false);
        if (getPhotos) getPhotos(converted);
      }
    };

    const handleSelfieCancel = () => setOpen(false);
    const handleSelfieBack = () => setOpen(false);

    smileIdElement.addEventListener(
      "selfie-capture-screens.publish",
      handleSelfieCapture as EventListener
    );
    smileIdElement.addEventListener(
      "selfie-capture-screens.cancelled",
      handleSelfieCancel as EventListener
    );
    smileIdElement.addEventListener(
      "selfie-capture-screens.back",
      handleSelfieBack as EventListener
    );

    return () => {
      smileIdElement.removeEventListener(
        "selfie-capture-screens.publish",
        handleSelfieCapture as EventListener
      );
      smileIdElement.removeEventListener(
        "selfie-capture-screens.cancelled",
        handleSelfieCancel as EventListener
      );
      smileIdElement.removeEventListener(
        "selfie-capture-screens.back",
        handleSelfieBack as EventListener
      );
    };
  }, [isComponentLoaded, open, getPhotos, setOpen]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] overflow-y-auto overflow-x-hidden bg-black bg-opacity-75">
      <div className="flex min-h-full items-center justify-center py-4">
        <style>{`.smile-card::-webkit-scrollbar{display:none}`}</style>
        <div
          className="smile-card relative mx-auto max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white px-4"
          style={
            {
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            } as React.CSSProperties
          }
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 rounded-full bg-white p-2 shadow-lg"
          >
            <CloseCircle width={24} height={24} />
          </button>
          <selfie-capture-screens
            ref={smileIdRef}
            theme-color={"#DA291C"}
            hide-back-to-host
          />
        </div>
      </div>
    </div>,
    document.body
  );
});

export default SmileIdCameraComponent;
